import { useState } from 'react';
import { Wrench, ChevronDown, ChevronRight, Check, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import type { ToolCall } from '../../types/chat';
import { formatToolName, formatContent } from './toolFormatters';
import { VideoPlayer, extractClipId, extractVideoUrl } from './VideoPlayer';

interface ToolCallBlockProps {
  toolCall: ToolCall;
}

export function ToolCallBlock({ toolCall }: ToolCallBlockProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isRunning = toolCall.status === 'running';

  return (
    <div className="mb-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={clsx(
          'flex items-center gap-2 w-full px-3 py-2 rounded-lg',
          'transition-colors text-sm font-medium',
          'border',
          isRunning
            ? 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200'
            : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200'
        )}
      >
        {isRunning ? (
          <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
        ) : (
          <Wrench className="w-4 h-4 text-emerald-500" />
        )}
        <span>{formatToolName(toolCall.name)}</span>
        {!isRunning && (
          <Check className="w-4 h-4 text-emerald-500 ml-1" />
        )}
        <span className="ml-auto">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </span>
      </button>
      {isExpanded && (
        <div className={clsx(
          'mt-2 rounded-lg border overflow-hidden',
          isRunning ? 'border-indigo-200' : 'border-emerald-200'
        )}>
          {/* Method name section */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Method
            </div>
            <code className="text-sm font-mono text-gray-800 bg-white px-2 py-1 rounded border border-gray-200">
              {toolCall.name}
            </code>
          </div>

          {/* Input parameters section */}
          {toolCall.input && Object.keys(toolCall.input).length > 0 && (
            <div className="px-4 py-3 bg-white border-b border-gray-200">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Parameters
              </div>
              <pre className="text-xs text-gray-700 bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto whitespace-pre">
                {formatContent(toolCall.input)}
              </pre>
            </div>
          )}

          {/* Result section */}
          {toolCall.result && (
            <div className="px-4 py-3 bg-white">
              {/* Video player for extract_video_clip results */}
              {toolCall.name === 'extract_video_clip' && extractClipId(toolCall.result) && (
                <VideoPlayer
                  clipId={extractClipId(toolCall.result)!}
                  videoUrl={extractVideoUrl(toolCall.result) ?? undefined}
                />
              )}

              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Result
              </div>
              <pre className="text-xs text-gray-700 bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre">
                {formatContent(toolCall.result)}
              </pre>
            </div>
          )}

          {/* Loading indicator for running tools */}
          {isRunning && !toolCall.result && (
            <div className="px-4 py-3 bg-white flex items-center gap-2 text-sm text-indigo-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Executing...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
